import { ChangeEvent, useState } from 'react'

import { Intent } from '../../config/intent'

export interface VerificationCodeInputProps {
    name: string
    onChange: (value: string) => void
    inputLength?: number
    intent?: Intent
}

export function VerificationCodeInput({
    name,
    onChange,
    inputLength = 6,
    intent = Intent.DEFAULT,
}: VerificationCodeInputProps) {
    const width = 340
    const height = 56
    const [mouseDown, setMouseDown] = useState(false)
    const [value, setValue] = useState('')
    const [cursor, setCursor] = useState<[number | null, number | null]>([
        null,
        null,
    ])
    const chars = value.split('')

    function handleChange(e: ChangeEvent<HTMLInputElement>) {
        const target = e.currentTarget
        const value = target.value
        if (value.length > inputLength) return
        setCursor([target.selectionStart, target.selectionEnd])
        setValue(value)
        onChange(value)
    }

    // any because there are multiple event types calling this func
    function setCurrentCursor(e: any) {
        setCursor([
            e.currentTarget.selectionStart,
            e.currentTarget.selectionEnd,
        ])
    }

    return (
        <div className='relative' style={{ width }}>
            <input
                name={name}
                id={name}
                className='absolute opacity-0 top-0 left-0 text-3xl border-0 font-bold px-2'
                type='text'
                style={{ width, height, letterSpacing: '1.1em' }}
                value={value}
                onChange={handleChange}
                onMouseUp={() => setMouseDown(false)}
                onMouseDown={() => setMouseDown(true)}
                onMouseMove={e => {
                    if (mouseDown) {
                        setCurrentCursor(e)
                    }
                }}
                onKeyUp={e => setCurrentCursor(e)}
                onClick={e => setCurrentCursor(e)}
                onDoubleClick={e => setCurrentCursor(e)}
                onFocus={e => setCurrentCursor(e)}
                onBlur={() => setCursor([null, null])}
                onPaste={() => setValue('')}
                data-testid='hidden-input'
                autoComplete='off'
            />
            <div
                className='flex items-stretch justify-around gap-4 pointer-events-none'
                style={{ width, height }}
                data-testid='display-wrapper'>
                {new Array(inputLength).fill(null).map((_, i) => {
                    const char = chars[i]
                    const className = [
                        'text-3xl font-bold px-2 py-2 text-center border-b-4 outline-none',
                        getBorderColorClass(intent, char),
                        getBgColorClass(i, cursor),
                    ]

                    return (
                        <div
                            key={i}
                            style={{ width: 36 }}
                            className={className.join(' ')}>
                            {char}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

function getBorderColorClass(intent: Intent, char: string | undefined) {
    if (intent !== Intent.DEFAULT) {
        return `border-${intent}`
    }
    if (char) {
        return 'border-black'
    }
    return 'border-gray'
}

function getBgColorClass(
    charIndex: number,
    cursor: [number | null, number | null],
) {
    if (cursor[0] === charIndex && cursor[1] === charIndex) {
        return 'bg-gray6'
    }
    if (cursor[0] !== null && cursor[1] !== null) {
        if (cursor[0] <= charIndex && cursor[1] > charIndex) {
            return 'bg-gray6'
        }
    }
    return ''
}
